import { useState, useMemo, useEffect, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useUser } from '@/contexts/UserContext'
import { useRequireAgendamentoPage } from '@/hooks/useAgendamentoFlags'
import { useToast } from '@/contexts/ToastContext'
import { GuestCadastroModal } from '@/components/GuestCadastroModal'
import {
  createAgendamentoAdmin,
  getUnidades,
  getProfissionais,
  getServicos,
  searchUsuariosAdmin,
  updateAgendamentoAdmin,
} from '@/api/adminApi'
import type { Unidade, Profissional, Servico, UsuarioSearchRow } from '@/api/adminApi'
import { isAdminClientPreviewMode } from '@/utils/adminAuth'
import { clearGuestSession, isGuestSessionActive } from '@/utils/guestAuth'
import { resolveMediaUrl } from '@/utils/mediaUrl'
import {
  createAgendamento,
  getDiasDisponiveisAgendamento,
  getHorariosDisponiveis,
  getHorariosOcupados,
  reagendarAgendamento,
} from '@/api/usuariosApi'
import { buildGoogleCalendarTemplateLink } from '@/utils/googleCalendarLink'
import {
  diaSemanaFromDateStr,
  getDiasDoMes,
  getNomeMes,
  isHorarioPassado,
} from '@/utils/date'
import styles from './AgendamentoPage.module.css'

const DIAS_SEMANA_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']

type ReagendarState = {
  agendamentoId: number
  profissional: string
  unidade: string
  servico_id?: number | null
}

export function AgendamentoPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const reagendar = (location.state as { reagendar?: ReagendarState } | null)?.reagendar ?? null
  const { loading: featureLoading } = useRequireAgendamentoPage()
  const { user, setUser } = useUser()
  const { toast } = useToast()
  const adminPreview = isAdminClientPreviewMode(user)
  const isGuest = isGuestSessionActive() && !user
  const [previewClienteQ, setPreviewClienteQ] = useState('')
  const [previewClientes, setPreviewClientes] = useState<UsuarioSearchRow[]>([])
  const [previewClienteId, setPreviewClienteId] = useState<number | null>(null)
  const [previewSearchLoading, setPreviewSearchLoading] = useState(false)
  const [unidades, setUnidades] = useState<Unidade[]>([])
  const [profissionais, setProfissionais] = useState<Profissional[]>([])
  const [loading, setLoading] = useState(true)
  const [step, setStep] = useState(1)
  const [unidadeId, setUnidadeId] = useState<number | null>(null)
  const [profissionalId, setProfissionalId] = useState<number | null>(null)
  const [filtroProfissional, setFiltroProfissional] = useState('')
  const [mesAtual, setMesAtual] = useState(() => new Date())
  const [dataSelecionada, setDataSelecionada] = useState<string | null>(null)
  const [horario, setHorario] = useState<string | null>(null)
  const [confirmado, setConfirmado] = useState(false)
  const [salvando, setSalvando] = useState(false)
  const [horariosOcupados, setHorariosOcupados] = useState<string[]>([])
  const [slotsBase, setSlotsBase] = useState<string[]>([])
  const [semHorarioNaUnidade, setSemHorarioNaUnidade] = useState(false)
  const [servicos, setServicos] = useState<Servico[]>([])
  const [servicoId, setServicoId] = useState<number | null>(null)
  const [loadingProfissionais, setLoadingProfissionais] = useState(false)
  const [diasSemanaAtendimento, setDiasSemanaAtendimento] = useState<number[] | null>(null)
  const [diasAtendimentoLabel, setDiasAtendimentoLabel] = useState<string[]>([])
  const [datasDisponiveis, setDatasDisponiveis] = useState<string[]>([])
  const [datasBloqueadas, setDatasBloqueadas] = useState<string[]>([])
  const [usaHorarioPadrao, setUsaHorarioPadrao] = useState(false)
  const [semAtendimentoNaUnidade, setSemAtendimentoNaUnidade] = useState(false)
  const [reagendarId, setReagendarId] = useState<number | null>(reagendar?.agendamentoId ?? null)
  const [reagendarPrefillDone, setReagendarPrefillDone] = useState(false)
  const [guestModalOpen, setGuestModalOpen] = useState(false)
  const [contaCriadaNoCheckout, setContaCriadaNoCheckout] = useState(false)
  const [googleCalendarLink, setGoogleCalendarLink] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    Promise.all([getUnidades(false), getServicos(false)])
      .then(([u, s]) => {
        if (!cancelled) {
          setUnidades(u)
          setServicos(s)
        }
      })
      .catch(() => {
        if (!cancelled) setUnidades([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (!reagendar || reagendarPrefillDone || unidades.length === 0) return
    const u = unidades.find((x) => x.nome === reagendar.unidade)
    if (u && unidadeId !== u.id) {
      setUnidadeId(u.id)
    }
  }, [reagendar, reagendarPrefillDone, unidades, unidadeId])

  useEffect(() => {
    if (!reagendar || reagendarPrefillDone || !unidadeId) return
    const p = profissionais.find((x) => x.nome === reagendar.profissional)
    if (p) {
      setProfissionalId(p.id)
      if (reagendar.servico_id) setServicoId(reagendar.servico_id)
      setReagendarId(reagendar.agendamentoId)
      setStep(3)
      setReagendarPrefillDone(true)
    }
  }, [reagendar, reagendarPrefillDone, unidadeId, profissionais])

  useEffect(() => {
    setProfissionalId(null)
    setDataSelecionada(null)
    setHorario(null)
    setSlotsBase([])
    if (!unidadeId) {
      setProfissionais([])
      return
    }
    let cancelled = false
    setLoadingProfissionais(true)
    getProfissionais(false, unidadeId)
      .then((p) => {
        if (!cancelled) setProfissionais(p)
      })
      .catch(() => {
        if (!cancelled) setProfissionais([])
      })
      .finally(() => {
        if (!cancelled) setLoadingProfissionais(false)
      })
    return () => { cancelled = true }
  }, [unidadeId])

  const unidade = unidadeId ? unidades.find((u) => u.id === unidadeId) : null
  const profissional = profissionalId ? profissionais.find((p) => p.id === profissionalId) : null

  const profissionaisFiltrados = useMemo(() => {
    if (!filtroProfissional.trim()) return profissionais
    const q = filtroProfissional.toLowerCase()
    return profissionais.filter(
      (p) =>
        p.nome.toLowerCase().includes(q) ||
        (p.especialidade ?? '').toLowerCase().includes(q)
    )
  }, [profissionais, filtroProfissional])

  const ano = mesAtual.getFullYear()
  const mes = mesAtual.getMonth()
  const diasCalendario = useMemo(() => getDiasDoMes(ano, mes), [ano, mes])

  const horariosDisponiveis = useMemo(() => {
    if (!dataSelecionada || slotsBase.length === 0) return []
    return slotsBase.filter((h) => !isHorarioPassado(dataSelecionada, h) && !horariosOcupados.includes(h))
  }, [dataSelecionada, horariosOcupados, slotsBase])

  useEffect(() => {
    let cancelled = false
    if (!profissionalId || !unidadeId) {
      setDiasSemanaAtendimento(null)
      setDiasAtendimentoLabel([])
      setDatasDisponiveis([])
      setDatasBloqueadas([])
      setUsaHorarioPadrao(false)
      setSemAtendimentoNaUnidade(false)
      return () => { cancelled = true }
    }
    getDiasDisponiveisAgendamento(profissionalId, unidadeId)
      .then(({ dias_semana, dias_label, datas_disponiveis, datas_bloqueadas, usa_padrao, sem_horario_na_unidade }) => {
        if (!cancelled) {
          setDiasSemanaAtendimento(dias_semana)
          setDiasAtendimentoLabel(dias_label)
          setDatasDisponiveis(datas_disponiveis)
          setDatasBloqueadas(datas_bloqueadas)
          setUsaHorarioPadrao(usa_padrao)
          setSemAtendimentoNaUnidade(sem_horario_na_unidade)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDiasSemanaAtendimento([])
          setDiasAtendimentoLabel([])
          setDatasDisponiveis([])
          setDatasBloqueadas([])
          setUsaHorarioPadrao(false)
          setSemAtendimentoNaUnidade(false)
        }
      })
    return () => { cancelled = true }
  }, [profissionalId, unidadeId])

  const diaTemAtendimento = useCallback(
    (dateStr: string) => {
      if (usaHorarioPadrao || diasSemanaAtendimento == null) return true
      if (datasBloqueadas.includes(dateStr)) return false
      if (datasDisponiveis.includes(dateStr)) return true
      if (semAtendimentoNaUnidade && datasDisponiveis.length === 0) return false
      const dow = diaSemanaFromDateStr(dateStr)
      return dow >= 0 && diasSemanaAtendimento.includes(dow)
    },
    [
      usaHorarioPadrao,
      diasSemanaAtendimento,
      datasBloqueadas,
      datasDisponiveis,
      semAtendimentoNaUnidade,
    ]
  )

  useEffect(() => {
    if (!dataSelecionada || diasSemanaAtendimento == null || usaHorarioPadrao) return
    if (!diaTemAtendimento(dataSelecionada)) {
      setDataSelecionada(null)
      setHorario(null)
    }
  }, [dataSelecionada, diasSemanaAtendimento, usaHorarioPadrao, diaTemAtendimento])

  useEffect(() => {
    let cancelled = false
    if (!dataSelecionada || !profissionalId || !unidadeId) {
      setSlotsBase([])
      setSemHorarioNaUnidade(false)
      return () => { cancelled = true }
    }
    getHorariosDisponiveis(profissionalId, dataSelecionada, unidadeId)
      .then(({ slots, sem_horario_na_unidade }) => {
        if (!cancelled) {
          setSlotsBase(slots)
          setSemHorarioNaUnidade(sem_horario_na_unidade)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSlotsBase([])
          setSemHorarioNaUnidade(false)
        }
      })
    return () => { cancelled = true }
  }, [dataSelecionada, profissionalId, unidadeId])

  useEffect(() => {
    let cancelled = false
    if (!dataSelecionada || !profissional || !unidade) {
      setHorariosOcupados([])
      return () => { cancelled = true }
    }
    getHorariosOcupados({
      data: dataSelecionada,
      profissional: profissional.nome,
      unidade: unidade.nome,
    })
      .then((horas) => {
        if (!cancelled) setHorariosOcupados(horas)
      })
      .catch(() => {
        if (!cancelled) setHorariosOcupados([])
      })
    return () => { cancelled = true }
  }, [dataSelecionada, profissional, unidade])

  useEffect(() => {
    if (horario && !horariosDisponiveis.includes(horario)) setHorario(null)
  }, [horario, horariosDisponiveis])

  const podeConfirmarStep3 =
    !semAtendimentoNaUnidade &&
    dataSelecionada !== null &&
    horario !== null &&
    (!adminPreview || previewClienteId != null)

  useEffect(() => {
    if (!adminPreview || step !== 3) return
    const q = previewClienteQ.trim()
    if (q.length < 2) {
      setPreviewClientes([])
      return
    }
    let cancelled = false
    const timer = window.setTimeout(() => {
      setPreviewSearchLoading(true)
      searchUsuariosAdmin(q)
        .then((rows) => {
          if (!cancelled) setPreviewClientes(rows)
        })
        .catch(() => {
          if (!cancelled) setPreviewClientes([])
        })
        .finally(() => {
          if (!cancelled) setPreviewSearchLoading(false)
        })
    }, 300)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [adminPreview, step, previewClienteQ])

  const submitAgendamentoCliente = async (opts?: {
    softAuth?: boolean
    contaNova?: boolean
  }) => {
    if (!profissional || !unidade || !dataSelecionada || !horario || !profissionalId || !unidadeId) return
    if (reagendarId) {
      await reagendarAgendamento(reagendarId, { data: dataSelecionada, hora: horario })
      toast('Agendamento remarcado com sucesso!', 'success')
      setGoogleCalendarLink(
        buildGoogleCalendarTemplateLink({
          title: `Agendamento — ${profissional.nome}`,
          data: dataSelecionada,
          hora: horario,
          details: `Unidade: ${unidade.nome}`,
        })
      )
    } else {
      const created = await createAgendamento(
        {
          data: dataSelecionada,
          hora: horario,
          profissional: profissional.nome,
          unidade: unidade.nome,
          profissional_id: profissionalId,
          unidade_id: unidadeId,
          servico_id: servicoId,
          status: 'agendado',
        },
        { softAuth: opts?.softAuth }
      )
      setGoogleCalendarLink(
        created.google_calendar_link ||
          buildGoogleCalendarTemplateLink({
            title: `Agendamento — ${profissional.nome}`,
            data: dataSelecionada,
            hora: horario,
            details: `Unidade: ${unidade.nome}`,
          })
      )
      if (opts?.contaNova) {
        toast(
          'Criámos a sua conta e enviámos para o seu e-mail o código de acesso e a confirmação da marcação.',
          { type: 'success', title: 'Marcação confirmada', durationMs: 9000 }
        )
      } else {
        toast(
          'Agendamento guardado com sucesso! Enviámos a confirmação para o seu e-mail.',
          'success'
        )
      }
    }
    setConfirmado(true)
  }

  const handleConfirmarAgendamento = async () => {
    if (!podeConfirmarStep3 || !profissional || !unidade || !dataSelecionada || !horario) return
    if (!profissionalId || !unidadeId) return
    setSalvando(true)
    try {
      if (adminPreview) {
        if (!previewClienteId) {
          toast('Seleccione o cliente para quem está a marcar.', 'error')
          return
        }
        if (reagendarId) {
          await updateAgendamentoAdmin(reagendarId, {
            data: dataSelecionada,
            hora: horario,
            profissional_id: profissionalId,
            unidade_id: unidadeId,
            servico_id: servicoId,
          })
          toast('Agendamento remarcado com sucesso!', 'success')
        } else {
          await createAgendamentoAdmin({
            usuario_id: previewClienteId,
            profissional_id: profissionalId,
            unidade_id: unidadeId,
            data: dataSelecionada,
            hora: horario,
            servico_id: servicoId,
            status: 'agendado',
          })
          toast('Agendamento criado para o cliente seleccionado.', 'success')
        }
        setConfirmado(true)
      } else if (isGuest) {
        setGuestModalOpen(true)
      } else {
        if (!user) {
          toast('Inicie sessão para confirmar o agendamento.', 'error')
          return
        }
        await submitAgendamentoCliente()
      }
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Erro ao guardar agendamento.', 'error')
    } finally {
      setSalvando(false)
    }
  }

  const handleGuestCadastroSuccess = async (checkout: {
    user: {
      id: number
      nome: string
      telefone: string | null
      email: string
      avatar_url?: string | null
    }
    contaNova: boolean
  }) => {
    setGuestModalOpen(false)
    setSalvando(true)
    setContaCriadaNoCheckout(checkout.contaNova)
    // Define o utilizador já — evita que RequireClientAuth expulse para o portal
    setUser({
      id: checkout.user.id,
      nome: checkout.user.nome,
      telefone: checkout.user.telefone,
      email: checkout.user.email,
      avatar_url: checkout.user.avatar_url ?? null,
    })
    try {
      await submitAgendamentoCliente({ softAuth: true, contaNova: checkout.contaNova })
      clearGuestSession()
    } catch (err) {
      // Mantém modo visitante + utilizador local para mostrar o erro sem redirect
      toast(
        err instanceof Error
          ? err.message
          : 'Não foi possível guardar o agendamento. Tente novamente.',
        'error'
      )
    } finally {
      setSalvando(false)
    }
  }

  const goPrevMonth = () => {
    setMesAtual((d) => new Date(d.getFullYear(), d.getMonth() - 1))
  }

  const goNextMonth = () => {
    setMesAtual((d) => new Date(d.getFullYear(), d.getMonth() + 1))
  }

  if (featureLoading) return null

  return (
    <main className={`${styles.page} animate-slide-up`}>
      <button
        type="button"
        className={styles.backLink}
        onClick={() => {
          if (step === 1) navigate('/menu')
          else if (confirmado) navigate('/menu')
          else setStep(step - 1)
        }}
      >
        ← Voltar
      </button>

      <div className={styles.card}>
        <div className={styles.steps}>
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`${styles.stepDot} ${step === s && !confirmado ? styles.active : ''}`}
              aria-hidden
            />
          ))}
        </div>

        {!confirmado && step === 1 && (
          <>
            <h1 className={styles.heading}>Escolha a unidade</h1>
            {loading ? (
              <p style={{ color: 'var(--text-muted)' }}>A carregar…</p>
            ) : unidades.length === 0 ? (
              <p style={{ color: 'var(--text-muted)' }}>Nenhuma unidade disponível.</p>
            ) : (
              <>
                <div className={styles.unidadeList}>
                  {unidades.map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      className={`${styles.unidadeOption} ${unidadeId === u.id ? styles.selected : ''}`}
                      onClick={() => setUnidadeId(u.id)}
                    >
                      <span className={styles.unidadeNome}>{u.nome}</span>
                      <span className={styles.morada}>{u.morada ?? ''}</span>
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className={styles.btnConfirmar}
                  disabled={!unidadeId}
                  onClick={() => setStep(2)}
                >
                  Confirmar
                </button>
              </>
            )}
          </>
        )}

        {!confirmado && step === 2 && (
          <>
            <h1 className={styles.heading}>Escolher profissional</h1>
            {servicos.length > 0 && (
              <div className={styles.filterRow} style={{ marginBottom: 12 }}>
                <span className={styles.filterLabel}>Serviço (opcional)</span>
                <select
                  className={styles.filterInput}
                  value={servicoId ?? ''}
                  onChange={(e) => setServicoId(e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">— Sem serviço específico —</option>
                  {servicos.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.nome}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {loadingProfissionais ? (
              <p style={{ color: 'var(--text-muted)' }}>A carregar profissionais…</p>
            ) : profissionais.length === 0 ? (
              <p style={{ color: 'var(--text-muted)' }}>
                Nenhum profissional com horários nesta unidade. Configure em Admin → Horários.
              </p>
            ) : (
              <>
                <div className={styles.searchBlock}>
                  <div className={styles.filterRow}>
                    <span className={styles.filterLabel}>Nome / Especialidade</span>
                    <input
                      type="search"
                      className={styles.filterInput}
                      placeholder="Digite para filtrar a lista…"
                      value={filtroProfissional}
                      onChange={(e) => setFiltroProfissional(e.target.value)}
                      aria-label="Buscar profissional por nome ou especialidade"
                    />
                  </div>
                  <p className={styles.searchResultLabel} aria-live="polite">
                    {profissionaisFiltrados.length === 0
                      ? 'Nenhum profissional encontrado.'
                      : `${profissionaisFiltrados.length} profissional${profissionaisFiltrados.length !== 1 ? 'eis' : ''}`
                    }
                  </p>
                  <div className={styles.profissionalList} role="listbox" aria-label="Lista de profissionais">
                    {profissionaisFiltrados.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        role="option"
                        aria-selected={profissionalId === p.id}
                        className={`${styles.profissionalOption} ${profissionalId === p.id ? styles.selected : ''}`}
                        onClick={() => setProfissionalId(p.id)}
                      >
                        {p.foto_url ? (
                          <img
                            src={resolveMediaUrl(p.foto_url)}
                            alt=""
                            className={styles.profissionalAvatar}
                          />
                        ) : (
                          <span className={styles.profissionalAvatarPh} aria-hidden>
                            {p.nome.trim().slice(0, 1).toUpperCase()}
                          </span>
                        )}
                        <div className={styles.profissionalInfo}>
                          <span className={styles.profissionalNome}>{p.nome}</span>
                          <span className={styles.profissionalEspecialidade}>
                            {p.especialidade ?? '—'}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
            <p className={styles.obs}>
              Você receberá um lembrete uma hora antes no dia do agendamento.
            </p>
            <button
              type="button"
              className={styles.btnConfirmar}
              disabled={!profissionalId}
              onClick={() => setStep(3)}
            >
              Confirmar
            </button>
          </>
        )}

        {!confirmado && step === 3 && profissional && (
          <>
            <h1 className={styles.heading}>Escolher dia e hora</h1>
            <p className={styles.profissionalEspecialidade} style={{ marginBottom: 16 }}>
              Profissional: {profissional.nome}
              {unidade ? ` · ${unidade.nome}` : ''}
            </p>
            {semAtendimentoNaUnidade ? (
              <p className={styles.obs} role="alert">
                Este profissional não tem horários configurados nesta unidade. Escolha outra unidade ou contacte o
                salão.
              </p>
            ) : (
              !usaHorarioPadrao && (
                <p className={styles.obs}>
                  {diasAtendimentoLabel.length > 0 && (
                    <>
                      Semanalmente: <strong>{diasAtendimentoLabel.join(', ')}</strong>
                      {datasDisponiveis.length > 0 ? ' · ' : ''}
                    </>
                  )}
                  {datasDisponiveis.length > 0 && (
                    <>
                      Datas especiais:{' '}
                      <strong>
                        {datasDisponiveis
                          .map((d) => d.split('-').reverse().join('/'))
                          .join(', ')}
                      </strong>
                    </>
                  )}
                </p>
              )
            )}

            <label className={styles.sectionLabel}>Data</label>
            <div className={styles.calendarWrap}>
              <div className={styles.calendarNav}>
                <button
                  type="button"
                  className={styles.calendarNavBtn}
                  onClick={goPrevMonth}
                  aria-label="Mês anterior"
                >
                  ‹
                </button>
                <span className={styles.calendarNavTitle}>
                  {getNomeMes(mes)} {ano}
                </span>
                <button
                  type="button"
                  className={styles.calendarNavBtn}
                  onClick={goNextMonth}
                  aria-label="Próximo mês"
                >
                  ›
                </button>
              </div>
              <div className={styles.calendarWeekdays}>
                {DIAS_SEMANA_LABELS.map((label) => (
                  <span key={label} className={styles.calendarWeekday}>
                    {label}
                  </span>
                ))}
              </div>
              <div className={styles.calendarGrid}>
                {diasCalendario.map((cell) => {
                  const disabled = cell.isPast || !diaTemAtendimento(cell.dateStr)
                  const selected = dataSelecionada === cell.dateStr
                  return (
                    <button
                      key={cell.dateStr}
                      type="button"
                      className={`${styles.calendarDay} ${cell.isCurrentMonth ? '' : styles.calendarDayOther} ${selected ? styles.calendarDaySelected : ''} ${disabled ? styles.calendarDayDisabled : ''}`}
                      disabled={disabled}
                      onClick={() => {
                        if (!disabled) {
                          setDataSelecionada(cell.dateStr)
                          setHorario(null)
                        }
                      }}
                    >
                      {cell.day}
                    </button>
                  )
                })}
              </div>
            </div>

            <label className={styles.sectionLabel}>Horário</label>
            <div className={styles.horariosGrid}>
              {horariosDisponiveis.map((h) => (
                <button
                  key={h}
                  type="button"
                  className={`${styles.horaBtn} ${horario === h ? styles.selected : ''}`}
                  onClick={() => setHorario(h)}
                >
                  {h}
                </button>
              ))}
            </div>
            {dataSelecionada && horariosDisponiveis.length === 0 && (
              <p className={styles.obs}>
                {semHorarioNaUnidade
                  ? datasBloqueadas.includes(dataSelecionada)
                    ? 'Neste dia o profissional atende noutra unidade (data específica). Escolha outra data ou unidade.'
                    : 'Este profissional não atende nesta unidade neste dia. Escolha outra data ou unidade.'
                  : 'Não há mais horários disponíveis para este dia.'}
              </p>
            )}

            {adminPreview && (
              <div className={styles.previewCliente}>
                <label className={styles.sectionLabel} htmlFor="preview-cliente-q">
                  Cliente
                </label>
                <p className={styles.obs}>
                  Na pré-visualização como administrador, escolha o cliente da marcação.
                </p>
                <input
                  id="preview-cliente-q"
                  type="search"
                  className={styles.previewClienteInput}
                  placeholder="Nome, e-mail ou telefone (mín. 2 letras)"
                  value={previewClienteQ}
                  onChange={(e) => {
                    setPreviewClienteQ(e.target.value)
                    setPreviewClienteId(null)
                  }}
                  autoComplete="off"
                />
                {previewSearchLoading && (
                  <p className={styles.obs}>A pesquisar clientes…</p>
                )}
                {previewClientes.length > 0 && (
                  <ul className={styles.previewClienteList}>
                    {previewClientes.map((c) => (
                      <li key={c.id}>
                        <button
                          type="button"
                          className={`${styles.previewClienteOption} ${
                            previewClienteId === c.id ? styles.previewClienteOptionSelected : ''
                          }`}
                          onClick={() => setPreviewClienteId(c.id)}
                        >
                          <span className={styles.previewClienteNome}>{c.nome}</span>
                          <span className={styles.previewClienteMeta}>
                            {[c.email, c.telefone].filter(Boolean).join(' · ')}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <button
              type="button"
              className={styles.btnConfirmar}
              disabled={!podeConfirmarStep3 || salvando}
              onClick={handleConfirmarAgendamento}
            >
              {salvando ? 'A guardar…' : 'Confirmar'}
            </button>
            <p className={styles.obsProfissional}>
              {adminPreview
                ? 'O cliente e o profissional recebem notificação após confirmar.'
                : 'Após você confirmar, o profissional recebe uma notificação.'}
            </p>
          </>
        )}

        {confirmado && profissional && unidade && dataSelecionada && horario && (
          <div className="animate-scale-in">
            <h1 className={styles.heading}>Agendamento confirmado</h1>
            <div className={styles.resumoAgendamento}>
              <p><strong>{profissional.nome}</strong></p>
              <p>{dataSelecionada} às {horario}</p>
              <p>{unidade.nome}</p>
            </div>
            <p className={styles.successMsg}>
              {contaCriadaNoCheckout
                ? 'Marcação feita com sucesso. Criámos a sua conta e enviámos para o seu e-mail o código de acesso e a confirmação da marcação.'
                : 'Enviámos a confirmação para o seu e-mail. O profissional foi notificado.'}
            </p>
            {contaCriadaNoCheckout && (
              <p className={styles.accountNotice}>
                Verifique a caixa de entrada (e o spam). Com o e-mail e o código pode entrar na app para
                consultar, remarcar ou cancelar.
              </p>
            )}
            {googleCalendarLink && (
              <a
                className={styles.googleCalBtn}
                href={googleCalendarLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                Abrir no Google Calendar
              </a>
            )}
            <p className={styles.accountNotice}>
              Se o profissional tiver o Google Calendar ligado, também pode receber um convite no seu
              e-mail.
            </p>
            <button
              type="button"
              className={styles.btnConfirmar}
              onClick={() => navigate('/menu')}
            >
              Voltar ao perfil
            </button>
          </div>
        )}
      </div>

      <GuestCadastroModal
        open={guestModalOpen}
        loading={salvando}
        onCancel={() => setGuestModalOpen(false)}
        onSuccess={handleGuestCadastroSuccess}
      />
    </main>
  )
}
